import { useState, useEffect } from "react";
import { Button, Badge, Label, TextInput, Select, ToggleSwitch } from "flowbite-react";
import { HiBan, HiEye, HiPencil, HiRefresh, HiTrash, HiFilter } from "react-icons/hi";
import { useNavigate } from "react-router-dom";
import { Agent, DataGridPagination } from "@/sharedTypes";
import DataTable from "@/components/Table";
import AgentFormDialog from "@/components/agents/AgentFormDialog";
import ConfirmationDialog from "@/components/ConfirmationDialog";
import {
  useDeleteAgentMutation,
  useEditAgentMutation,
  useHardDeleteAgentMutation,
  useRestoreAgentMutation,
} from "@/api/agentApi/agent";
import { agentRoles, agentRoleLabels, agentStatusLabels, agentStatus } from "@/constants/agent";
import { getErrorMessage } from "@/utils/requestUtils";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useCurrentUser } from "@/hooks/useCurrentUser";

interface AgentListTableProps {
  data: Agent[];
  pagination: DataGridPagination;
  onPageChange: (page: number) => void;
  filters: any;
  onFilterChange: (filters: any) => void;
}

export default function AgentListTable({
  data,
  pagination,
  onPageChange,
  filters,
  onFilterChange,
}: AgentListTableProps) {
  const { t } = useTranslation(["agent", "common"]);
  const user = useCurrentUser();
  const isAdmin = user?.agent_role === agentRoles.company_admin;
  const navigate = useNavigate();
  
  const [showFilters, setShowFilters] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [usernameFilter, setUsernameFilter] = useState("");
  const [emailFilter, setEmailFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  
  const [debouncedUsername, setDebouncedUsername] = useState("");
  const [debouncedEmail, setDebouncedEmail] = useState("");

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<Agent | undefined>();
  const [confirmDialog, setConfirmDialog] = useState<{
    title: string;
    body: string;
    action: "disable" | "restore" | "hardDelete";
    onConfirm: () => void;
  } | null>(null);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedUsername(usernameFilter);
      setDebouncedEmail(emailFilter);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [usernameFilter, emailFilter]);

  useEffect(() => {
    const activeFilters: Record<string, any> = { ...filters, is_active: !showInactive };

    if (debouncedUsername) {
      activeFilters["agent_username"] = debouncedUsername;
    } else {
      delete activeFilters["agent_username"];
    }

    if (debouncedEmail) {
      activeFilters["agent_email"] = debouncedEmail;
    } else {
      delete activeFilters["agent_email"];
    }

    if (roleFilter) {
      activeFilters.agent_role = parseInt(roleFilter);
    } else {
      delete activeFilters.agent_role;
    }

    if (statusFilter) {
        activeFilters.agent_status = parseInt(statusFilter);
    } else {
        delete activeFilters.agent_status;
    }

    onFilterChange(activeFilters);
  }, [showInactive, debouncedUsername, debouncedEmail, roleFilter, statusFilter]);

  const handleResetFilters = () => {
    setUsernameFilter("");
    setEmailFilter("");
    setRoleFilter("");
    setStatusFilter("");
    setShowInactive(false);
  };

  const handleToggleFilters = () => {
    if (showFilters) {
        handleResetFilters();
    }
    setShowFilters(!showFilters);
  };

  const [editAgent] = useEditAgentMutation();
  const [deleteAgent, { isLoading: isDeleting }] = useDeleteAgentMutation();
  const [restoreAgent, { isLoading: isRestoring }] = useRestoreAgentMutation();
  const [hardDeleteAgent, { isLoading: isHardDeleting }] =
    useHardDeleteAgentMutation();

  const handleEdit = (agent: Agent) => {
    setSelectedAgent(agent);
    setIsEditModalOpen(true);
  };

  const handleEditSubmit = (updated: any) => {
    if (!selectedAgent?.agent_id) return;
    
    const payload: any = {
      agent_email: updated.agent_email,
      agent_password: updated.agent_password,
      agent_username: updated.agent_username,
      display_name: updated.display_name,
      shift_id: updated.shift_id ? parseInt(updated.shift_id, 10) : undefined,
    };

    // Remove undefined or empty password if not provided (to avoid overwriting with empty string)
    if (!payload.agent_password) {
        delete payload.agent_password;
    }

    if (updated.group_ids) {
      payload.group_ids = updated.group_ids;
    }

    if (updated.job_roles) {
      payload.job_roles = updated.job_roles;
    }

    editAgent({
      agentId: selectedAgent.agent_id.toString(),
      updatedDetails: payload,
    })
      .unwrap()
      .then((res: any) => {
        const skipped = res?.skipped_groups ?? res?.data?.skipped_groups;
        if (skipped?.length) {
          toast.warning(t("agent:someGroupsSkipped"));
        }

        toast.success(t("agent:updateSuccess"));
        setIsEditModalOpen(false);
      })
      .catch((err) => toast.error(getErrorMessage(err)));
  };

  const handleDisable = (id: string) => {
    setConfirmDialog({
      title: t("agent:disableConfirmTitle"),
      body: t("agent:disableConfirmBody"),
      action: "disable",
      onConfirm: () => {
        deleteAgent(id)
          .unwrap()
          .then(() => {
            toast.success(t("agent:disableSuccess"));
            setConfirmDialog(null);
          })
          .catch((err) => toast.error(getErrorMessage(err)));
      },
    });
  };

  const handleRestore = (id: number) => {
    setConfirmDialog({
      title: t("agent:restoreConfirmTitle"),
      body: t("agent:restoreConfirmBody"),
      action: "restore",
      onConfirm: () => {
        restoreAgent(id)
          .unwrap()
          .then(() => {
            toast.success(t("agent:restoreSuccess"));
            setConfirmDialog(null);
          })
          .catch((err) => toast.error(getErrorMessage(err)));
      },
    });
  };

  const handleHardDelete = (id: number) => {
    setConfirmDialog({
      title: t("agent:deleteConfirmTitle"),
      body: t("agent:deleteConfirmBody"),
      action: "hardDelete",
      onConfirm: () => {
        hardDeleteAgent(id)
          .unwrap()
          .then(() => {
            toast.success(t("agent:deleteSuccess"));
            setConfirmDialog(null);
          })
          .catch((err) => toast.error(getErrorMessage(err)));
      },
    });
  };

  const getRoleBadgeColor = (role: number) => {
    switch (role) {
      case 1: // Agent
        return "info";
      case 2: // Company Admin
        return "warning";
      case 3: // Super Admin
        return "purple";
      case 10: // Frontline
        return "info";
      case 11: // Backoffice
        return "indigo";
      case 12: // Management
        return "purple";
      case 13: // Other
        return "gray";
      default:
        return "gray";
    }
  };

  const getStatusBadgeColor = (status: number) => {
    switch (status) {
      case 13: // Online
        return "success";
      case 14: // Offline
        return "gray";
      default:
        return "gray";
    }
  };

  const columns = [
    { key: "belongs_to_company_id", label: t("agent:companyId") },
    { key: "agent_username", label: t("agent:username") },
    {
      key: "display_name",
      label: t("agent:displayName"),
      render: (row: Agent) => <span>{row.display_name || "—"}</span>,
    },
    { key: "agent_email", label: t("agent:agentEmail") },
    {
      key: "agent_role",
      label: t("agent:role"),
      render: (row: Agent) => (
        <Badge color={getRoleBadgeColor(row.agent_role ?? 0)} className="w-fit">
          {t(`agent:roles.${row.agent_role ?? 0}` as any, { defaultValue: t("agent:unknown") })}
        </Badge>
      ),
    },
    {
      key: "job_roles",
      label: t("agent:jobRoles", { defaultValue: "Job Role" }),
      render: (row: Agent) => {
        const raw = row.job_roles;
        const roles: number[] = Array.isArray(raw)
          ? raw.map((r: any) => (typeof r === "object" && r !== null ? r.role : r)).filter((r: any) => typeof r === "number")
          : [];
        if (!roles.length) {
          return <span className="text-gray-400 text-sm">—</span>;
        }
        // Single-select: render the first role. (Junction can hold multiple
        // historically; we surface the active one.)
        const r = roles[0];
        return (
          <Badge color={getRoleBadgeColor(r)} className="w-fit">
            {agentRoleLabels[r] ?? r}
          </Badge>
        );
      },
    },
    {
      key: "groups",
      label: t("agent:groups"),
      render: (row: Agent) =>
        row.groups?.length ? (
          <div className="flex flex-wrap gap-1">
            {row.groups.map((g) => (
              <Badge key={g.group_id} color="purple" className="w-fit">
                {g.group_name}
              </Badge>
            ))}
          </div>
        ) : (
          <span className="text-gray-400 text-sm">—</span>
        ),
    },
   
    {
      key: "actions",
      label: t("agent:actions"),
      render: (row: Agent) => (
        <div className="flex gap-2">
          <Button size="xs" color="light" onClick={() => navigate(`/agents/${row.agent_id}`)}>
            <HiEye className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          </Button>
          {isAdmin &&
            (showInactive ? (
              <>
                <Button
                  size="xs"
                  color="light"
                  onClick={() => handleRestore(row.agent_id)}
                >
                  <HiRefresh className="h-4 w-4 text-green-600 dark:text-green-400" />
                </Button>
                <Button
                  size="xs"
                  color="light"
                  onClick={() => handleHardDelete(row.agent_id)}
                >
                  <HiTrash className="h-4 w-4 text-red-600 dark:text-red-400" />
                </Button>
              </>
            ) : (
              <>
                <Button size="xs" color="light" onClick={() => handleEdit(row)}>
                  <HiPencil className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                </Button>
                {row.agent_role !== 2 && (
                  <Button
                    size="xs"
                    color="light"
                    onClick={() => handleDisable(row.agent_id.toString())}
                  >
                    <HiBan className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                  </Button>
                )}
              </>
            ))}
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="mb-6 flex justify-start">
        <Button color="light" onClick={handleToggleFilters}>
          <HiFilter className="mr-2 h-5 w-5" />
          {showFilters ? t("common:hideFilters") : t("common:showFilters")}
        </Button>
      </div>

      {showFilters && (
        <div className="mb-6 rounded-lg bg-white p-4 shadow dark:bg-gray-800">
          <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
            {t("common:filters")}
          </h3>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            <div>
              <Label htmlFor="username-filter" className="mb-2 block">
                {t("agent:username")}
              </Label>
              <TextInput
                id="username-filter"
                placeholder={t("agent:username")}
                value={usernameFilter}
                onChange={(e) => setUsernameFilter(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="email-filter" className="mb-2 block">
                {t("agent:agentEmail")}
              </Label>
              <TextInput
                id="email-filter"
                placeholder={t("agent:agentEmail")}
                value={emailFilter}
                onChange={(e) => setEmailFilter(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="role-filter" className="mb-2 block">
                {t("agent:role")}
              </Label>
              <Select
                id="role-filter"
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
              >
                <option value="">{t("common:all") || "All"}</option>
                {Object.entries(agentRoleLabels).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor="status-filter" className="mb-2 block">
                {t("agent:status")}
              </Label>
              <Select
                id="status-filter"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">{t("common:all") || "All"}</option>
                {Object.entries(agentStatusLabels).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            </div>

            <div className="flex flex-col justify-end gap-4 pb-2">
              <ToggleSwitch
                checked={showInactive}
                label={t("agent:showInactive")}
                onChange={setShowInactive}
              />
              <Button color="gray" onClick={handleResetFilters}>
                {t("common:reset") || "Reset"}
              </Button>
            </div>
          </div>
        </div>
      )}

      <DataTable
        columns={columns}
        data={data}
        pagination={pagination}
        onPageChange={onPageChange}
      />

      {isEditModalOpen && (
        <AgentFormDialog
          open={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSubmit={handleEditSubmit}
          mode="edit"
          initialData={selectedAgent}
        />
      )}

      {confirmDialog && (
        <ConfirmationDialog
          title={confirmDialog.title}
          body={confirmDialog.body}
          onCancel={() => setConfirmDialog(null)}
          onConfirm={confirmDialog.onConfirm}
          isLoading={
            confirmDialog.action === "disable"
              ? isDeleting
              : confirmDialog.action === "restore"
                ? isRestoring
                : isHardDeleting
          }
        />
      )}
    </div>
  );
}
